import React, { useEffect, useState } from "react";
import { Loader2, Plus, X, Check, User, BadgeDollarSign, Calculator } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";

const CONDITIONS = ["New", "Like New", "Excellent", "Acceptable", "Poor", "Sport", "Not Working"];
const COMPONENTS_OPTS = ["CIB", "Item & Box", "Loose"];
const TIERS = [0, 1, 2, 3];

export default function TradeTerminal() {
  const navigate = useNavigate();

  // tab toggle (cash | trade)
  const [quoteType, setQuoteType] = useState("trade");

  // attached member
  const [member, setMember] = useState(null);
  const [showAccountPrompt, setShowAccountPrompt] = useState(true);
  const [promptInput, setPromptInput] = useState("");
  const [promptError, setPromptError] = useState("");

  // add-item form
  const [consoles, setConsoles] = useState([]);
  const [form, setForm] = useState({
    title: "",
    console: "",
    components: COMPONENTS_OPTS[0],
    condition: "Excellent",
    tier: 1,
    base_value_override: "",
  });
  const [evaluating, setEvaluating] = useState(false);

  // cart
  const [cart, setCart] = useState([]); // each row carries the enriched payload + display values

  const [confirming, setConfirming] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/consoles").then((r) => {
      setConsoles(r.data);
      setForm((p) => ({ ...p, console: r.data?.[0]?.name || "" }));
    });
  }, []);

  // When quoteType changes, recompute each existing cart item server-side so values flip.
  useEffect(() => {
    if (cart.length === 0) return;
    (async () => {
      const next = [];
      for (const row of cart) {
        try {
          const r = await api.post(`/trade-terminal/evaluate?quote_type=${quoteType}`, {
            title: row.title, console: row.console, components: row.components,
            condition: row.condition, tier: Number(row.tier ?? 1),
            base_value_override: row.base_value_override || null,
          });
          next.push({ ...row, ...r.data });
        } catch {
          next.push(row);
        }
      }
      setCart(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteType]);

  const valKey = quoteType === "cash" ? "cash_value" : "trade_value";
  const totalValue = cart.reduce((s, it) => s + (Number(it[valKey]) || 0), 0);

  const lookupMember = async (phone) => {
    setPromptError("");
    try {
      const r = await api.get(`/admin/members/${encodeURIComponent(phone.trim())}`);
      setMember(r.data);
      setShowAccountPrompt(false);
      setPromptInput("");
    } catch (err) {
      setPromptError(err.response?.status === 404 ? "No account found." : "Lookup failed.");
    }
  };

  const addItem = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || !form.console) return;
    setEvaluating(true);
    setMsg("");
    try {
      const payload = {
        title: form.title.trim(),
        console: form.console,
        components: form.components,
        condition: form.condition,
        tier: Number(form.tier),
        base_value_override: form.base_value_override ? Number(form.base_value_override) : null,
      };
      const r = await api.post(`/trade-terminal/evaluate?quote_type=${quoteType}`, payload);
      if (r.data.lookup_status === "not_found" && !payload.base_value_override) {
        setMsg("No PriceCharting data for that title — enter a manual base value to continue.");
        setEvaluating(false);
        return;
      }
      setCart((p) => [...p, { ...r.data, ...payload }]);
      setForm({ ...form, title: "", base_value_override: "" });
    } catch (err) {
      setMsg(err.response?.data?.detail || "Failed to evaluate item.");
    } finally {
      setEvaluating(false);
    }
  };

  const removeRow = (idx) => setCart((p) => p.filter((_, i) => i !== idx));

  const resetAll = () => {
    setCart([]); setMember(null); setShowAccountPrompt(true); setPromptInput("");
    setPromptError(""); setMsg("");
    setForm((p) => ({ ...p, title: "", base_value_override: "" }));
  };

  const confirmTrade = async () => {
    if (cart.length === 0) return;
    setConfirming(true);
    try {
      const r = await api.post("/trade-terminal/confirm", {
        quote_type: quoteType,
        member_phone: member?.phone || null,
        items: cart.map((it) => ({
          title: it.title,
          console: it.console,
          components: it.components,
          condition: it.condition,
          tier: Number(it.tier ?? 1),
          base_value_override: it.base_value_override ? Number(it.base_value_override) : null,
        })),
      });
      const parts = [`Trade complete — ${r.data.items_added_to_inventory} item(s) to inventory.`];
      if (r.data.trade_credit_awarded) parts.push(`+$${r.data.trade_credit_awarded} trade credit to ${member.first_name}.`);
      setMsg(parts.join(" "));
      setConfirmModal(false);
      resetAll();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to confirm.");
      setConfirmModal(false);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <button onClick={() => navigate("/pos/dashboard")} className="label-eyebrow inline-block" data-testid="trade-back">← Back to Dashboard</button>
          <div className="flex gap-2">
            <button onClick={() => navigate("/pos/terminal")} className="btn-secondary inline-flex items-center gap-2" data-testid="trade-toggle-pos"><Calculator size={14} /> POS Terminal</button>
            <button className="btn-primary inline-flex items-center gap-2" disabled data-testid="trade-active"><BadgeDollarSign size={14} /> Trade Terminal</button>
          </div>
        </div>
        <h1 className="font-display text-4xl font-black mb-6">Trade Terminal</h1>

        {/* Cash / Trade toggle */}
        <div className="surface inline-flex p-1 mb-6" data-testid="trade-type-toggle">
          {["trade", "cash"].map((t) => (
            <button
              key={t}
              onClick={() => setQuoteType(t)}
              className="px-5 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
              style={{
                background: quoteType === t ? "var(--ab-primary)" : "transparent",
                color: quoteType === t ? "#fff" : "var(--ab-text-dim)",
              }}
              data-testid={`trade-type-${t}`}
            >
              {t === "trade" ? "Trade-In Credit" : "Cash Out"}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT — add-item form */}
          <div className="lg:col-span-2 space-y-3">
            <form onSubmit={addItem} className="surface p-5 space-y-3" data-testid="trade-add-form">
              <div className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Add Item</div>
              <input
                className="input-terminal"
                placeholder="Scan barcode or type game title…"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                data-testid="trade-input-title"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <select className="input-terminal" value={form.console} onChange={(e) => setForm({ ...form, console: e.target.value })} data-testid="trade-input-console">
                  {consoles.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
                <select className="input-terminal" value={form.components} onChange={(e) => setForm({ ...form, components: e.target.value })} data-testid="trade-input-components">
                  {COMPONENTS_OPTS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <select className="input-terminal" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="trade-input-condition">
                  {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select className="input-terminal" value={form.tier} onChange={(e) => setForm({ ...form, tier: Number(e.target.value) })} data-testid="trade-input-tier">
                  {TIERS.map((t) => <option key={t} value={t}>Tier {t}</option>)}
                </select>
              </div>
              <input
                className="input-terminal"
                type="number"
                min="0"
                step="1"
                placeholder="Base $ override (optional — use when PriceCharting lookup fails)"
                value={form.base_value_override}
                onChange={(e) => setForm({ ...form, base_value_override: e.target.value })}
                data-testid="trade-input-override"
              />
              <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={evaluating} data-testid="trade-add-btn">
                {evaluating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add to Trade
              </button>
            </form>
            {msg && <div className="surface p-4 text-sm" data-testid="trade-msg">{msg}</div>}
          </div>

          {/* RIGHT — member + cart */}
          <div className="space-y-3">
            <div className="surface p-5" data-testid="trade-member-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow inline-flex items-center gap-2" style={{ color: "var(--ab-primary)" }}>
                  <BadgeDollarSign size={12} /> AB Member
                </div>
                {member && <button onClick={() => { setMember(null); setShowAccountPrompt(true); }} className="text-xs label-eyebrow hover:text-[var(--ab-danger)]" data-testid="trade-member-clear">Remove</button>}
              </div>
              {!member ? (
                <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>Walk-in trade (no member attached).</div>
              ) : (
                <div>
                  <div className="font-display text-xl font-bold flex items-center gap-2"><User size={18} /> {member.first_name} {member.last_name}</div>
                  <div className="font-mono text-xs mt-2" style={{ color: "var(--ab-text-muted)" }}>
                    Trade Credit: <b style={{ color: "var(--ab-primary)" }}>${Number(member.trade_credit || 0).toLocaleString()}</b>
                  </div>
                  {quoteType === "trade" && totalValue > 0 && (
                    <div className="font-mono text-xs mt-1" style={{ color: "var(--ab-text-muted)" }}>
                      Earning <b style={{ color: "var(--ab-primary)" }}>+${totalValue.toLocaleString()}</b> trade credit
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="surface flex flex-col">
              <div className="px-5 py-4 border-b flex justify-between" style={{ borderColor: "var(--ab-border)" }}>
                <span className="label-eyebrow">Trade Items</span>
                <span className="label-eyebrow">{cart.length} item{cart.length === 1 ? "" : "s"}</span>
              </div>
              <div className="flex-1 max-h-[55vh] overflow-auto">
                {cart.length === 0 ? (
                  <div className="p-6 text-sm" style={{ color: "var(--ab-text-dim)" }}>No items yet.</div>
                ) : (
                  cart.map((it, idx) => (
                    <div key={idx} className="flex justify-between items-center px-5 py-3 border-b" style={{ borderColor: "var(--ab-border)" }} data-testid="trade-cart-row">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold truncate">{it.title}</div>
                        <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>
                          {it.console} · {it.condition} · {it.components} · Tier {it.tier ?? 1}
                        </div>
                      </div>
                      <div className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>${Number(it[valKey] || 0)}</div>
                      <button onClick={() => removeRow(idx)} className="ml-3 inline-flex items-center justify-center" style={{ width: 28, height: 28, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)", background: "rgba(239,68,68,0.08)" }} data-testid={`trade-remove-${idx}`}><X size={14} /></button>
                    </div>
                  ))
                )}
              </div>
              <div className="px-5 py-4 border-t flex justify-between items-center" style={{ borderColor: "var(--ab-border)", background: "var(--ab-surface-2)" }}>
                <span className="label-eyebrow">Total {quoteType === "trade" ? "Trade Value" : "Cash Out"}</span>
                <span className="font-mono text-2xl font-bold" style={{ color: "var(--ab-primary)" }} data-testid="trade-total">${totalValue.toLocaleString()}</span>
              </div>
              <div className="p-4 grid grid-cols-2 gap-2">
                <button onClick={resetAll} disabled={cart.length === 0} className="btn-danger" data-testid="trade-cancel">Cancel</button>
                <button onClick={() => setConfirmModal(true)} disabled={cart.length === 0 || confirming} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="trade-confirm">
                  {confirming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account prompt at start of each trade */}
      {showAccountPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} data-testid="trade-account-prompt">
          <form
            onSubmit={(e) => { e.preventDefault(); if (promptInput.trim()) lookupMember(promptInput); }}
            className="surface p-7 max-w-md w-full"
          >
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Step 1</div>
            <h3 className="font-display text-2xl font-bold mb-2">AB Member Account?</h3>
            <p className="text-sm mb-5" style={{ color: "var(--ab-text-muted)" }}>
              On a TRADE-IN quote, their trade value is added directly to their account as trade credit.
            </p>
            <input
              className="input-terminal mb-3"
              placeholder="Account # (phone)"
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              autoFocus
              data-testid="trade-account-input"
            />
            {promptError && <div className="text-sm mb-3" style={{ color: "var(--ab-danger)" }} data-testid="trade-account-error">{promptError}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAccountPrompt(false)} className="btn-secondary" data-testid="trade-account-skip">Skip (Walk-In)</button>
              <button type="submit" className="btn-primary" data-testid="trade-account-attach">Attach</button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => !confirming && setConfirmModal(false)}>
          <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="trade-confirm-modal">
            <h3 className="font-display text-2xl font-bold mb-2">Confirm {quoteType === "trade" ? "Trade-In" : "Cash Out"}</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>
              {cart.length} item(s) worth <b>${totalValue.toLocaleString()}</b> {quoteType === "trade" ? "trade credit" : "cash out"}.
              {member && quoteType === "trade" && <> Credit will be added to {member.first_name}'s account.</>}
              {" "}Items will be added to inventory.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmModal(false)} disabled={confirming} className="btn-secondary" data-testid="trade-confirm-no">Cancel</button>
              <button onClick={confirmTrade} disabled={confirming} className="btn-primary inline-flex items-center gap-2" data-testid="trade-confirm-yes">
                {confirming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
